// Single source of truth for the public marketing site's technical SEO.
//
// This module is intentionally dependency-free (no `@/` aliases, no
// `import.meta`, no React) so it can be imported both by the app *and* by the
// Vite build (see the sitemap plugin in vite.config.ts) without pulling in the
// browser runtime. Everything that needs to know the set of public routes —
// the router, the per-page canonical/OG URLs, and the generated sitemap — reads
// from here, so those three can never drift apart.

/** Canonical production origin. Canonicals always point here regardless of the
 *  environment the bundle runs in (dev/preview/prod), which is correct SEO
 *  practice — a preview build must not advertise itself as the canonical. */
export const SITE_URL = 'https://astron.health';

export const SITE_NAME = 'Astron Health';

/** Original patient leaflet, served unchanged as a standalone PDF. */
export const LEAFLET_PDF_PATH = '/guides/astron-health-second-opinion-patient-leaflet.pdf';

export function isLeafletPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '').toLowerCase() === PUBLIC_ROUTES.leaflet.path;
}

/** Open Graph locale. Astron Health Ltd is UK-registered; the site defaults to
 *  British English (see `<html lang="en">`). */
export const SITE_LOCALE = 'en_GB';

/** Default social share image (1200×630), served from /public. */
export const DEFAULT_OG_IMAGE = '/og-default.png';

/** Theme colour used for the browser chrome and the web app manifest. */
export const THEME_COLOR = '#1a3a5c';

/** Last-modified date advertised in the sitemap. Bump when public content
 *  changes materially. */
export const SITE_LASTMOD = '2026-07-22';

export type ChangeFreq =
  | 'always'
  | 'hourly'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'yearly'
  | 'never';

export interface PublicRoute {
  /** Route path pattern as understood by the router (may contain `:params`). */
  path: string;
  /** Whether search engines may index the page. Non-indexable pages are marked
   *  `noindex` and excluded from the sitemap. */
  indexable: boolean;
  /** Sitemap hints (only used for indexable, parameter-free routes). */
  changefreq?: ChangeFreq;
  priority?: number;
  lastmod?: string;
}

/**
 * Every public marketing route, keyed by a stable name. The router references
 * `PUBLIC_ROUTES.<name>.path` so a path can only be defined in one place, and
 * the sitemap is generated from this same map.
 */
export const PUBLIC_ROUTES = {
  home: { path: '/', indexable: true, changefreq: 'weekly', priority: 1.0 },
  faq: { path: '/faq', indexable: true, changefreq: 'monthly', priority: 0.7 },
  science: { path: '/science', indexable: true, changefreq: 'monthly', priority: 0.8 },
  // Full molecular tumour board — destination of the hero board panel's
  // "Learn more about the board" link (Task #313). Renders the Science page
  // scrolled to the board section, mirroring the /faq → HomePage pattern.
  scienceBoard: { path: '/science/board', indexable: true, changefreq: 'monthly', priority: 0.6 },
  clinicians: { path: '/clinicians', indexable: true, changefreq: 'monthly', priority: 0.8 },
  // Former ads landing page, now a redirect to /#book-call (the shared call
  // flow on the homepage) — a redirect URL, not a content page, so it stays
  // out of the index/sitemap.
  bookACall: { path: '/book-a-call', indexable: false },
  // Former ads landing page, now a redirect to /science#sample-report (the
  // Science page's full sample report) — a redirect URL, not a content page,
  // so it stays out of the index/sitemap.
  freeSampleReport: { path: '/free-sample-report', indexable: false },
  terms: { path: '/terms', indexable: true, changefreq: 'yearly', priority: 0.3 },
  privacy: { path: '/privacy', indexable: true, changefreq: 'yearly', priority: 0.3 },
  healthData: { path: '/health-data', indexable: true, changefreq: 'yearly', priority: 0.3 },
  cookies: { path: '/cookies', indexable: true, changefreq: 'yearly', priority: 0.3 },
  // Public contact page (TCR review): single-step contact form with separate,
  // unbundled SMS + email consent checkboxes. Must load with no modals and no
  // geo variation — The Campaign Registry visits the URL directly.
  contact: { path: '/contact', indexable: true, changefreq: 'monthly', priority: 0.6 },
  // Short booking link: redirects to the Calendly scheduling page. A
  // convenience/redirect URL, not a content page — out of the index/sitemap.
  book: { path: '/book', indexable: false },
  // Campaign entry point in circulation (emails + campaign links): opens the
  // purchase flow over the homepage. Deliberately out of the index/sitemap —
  // it's a marketing entry URL, not a content page.
  secondOpinion: { path: '/second-opinion', indexable: false },
  // Direct purchase entry point for warm prospects: opens the product-details
  // checkout without the clinical questionnaire. It remains in the URL while
  // the flow is open so refresh can restore the same direct experience.
  directSecondOpinion: { path: '/second-opinion/checkout', indexable: false },
  // Stripe Payment Link completion redirect (Task #252): Stripe sends the
  // visitor here after a successful pre-signup card payment
  // (?session_id={CHECKOUT_SESSION_ID}). The app finalizes client-side and
  // resumes the purchase flow at account creation. Never indexed.
  purchaseComplete: { path: '/purchase/complete', indexable: false },
  // Lead-magnet delivery page: linked from the guide email, deliberately kept
  // out of the index and sitemap so the guide stays behind the email signup.
  oncologistGuide: {
    path: '/guides/questions-to-ask-your-oncologist',
    indexable: false,
  },
  // Redirect alias for the original standalone PDF; never indexed.
  leaflet: { path: '/leaflet', indexable: false },
  // Personalised next-steps quiz funnel (Task #469): a stable public
  // destination for site buttons ("What are your next steps?"). A funnel
  // entry, not a content page — deliberately out of the index and sitemap.
  nextSteps: { path: '/next-steps', indexable: false },
  // Quiz ads landing page (Task #485): a trimmed campaign page whose single
  // action is opening the existing trust flow ("See how Astron would work for
  // you"). A paid-traffic entry URL, not a content page — never indexed, never
  // in the sitemap.
  quiz: { path: '/quiz', indexable: false },
  // Affiliate tracked homepage URL (Tend the Terrain): renders the ordinary
  // homepage unchanged; the only difference is that landing here records
  // "Tend the Terrain" as the visitor's first-touch source. Declares `/` as
  // its canonical and stays out of the index and sitemap so it can never
  // compete with the homepage in search.
  tendTheTerrain: { path: '/tend-the-terrain', indexable: false },
  // Live sessions directory (Task #432): the permanent public URL for
  // Astron's live patient sessions — indexable, in the sitemap.
  live: { path: '/live', indexable: true, changefreq: 'weekly', priority: 0.7 },
  // Explainer landing page (Task #463/#523): four-minute YouTube explainer,
  // supporting sections and "Book a call" CTAs, on the standard public shell.
  // Indexable, in the sitemap.
  introduction: {
    path: '/introduction-to-astron',
    indexable: true,
    changefreq: 'monthly',
    priority: 0.6,
    lastmod: '2026-09-08',
  },
  // First-party webinar registration (Task #449): keyed by an unguessable
  // webinar identifier, linked from /live cards and invitation emails —
  // never indexed or in the sitemap.
  liveRegister: { path: '/live/register/:key', indexable: false },
  // One-click unsubscribe for webinar registrant mail (reminders/recording).
  liveUnsubscribe: { path: '/live/unsubscribe/:registrationId', indexable: false },
  // Live sessions admin (Task #432): same standalone ops pattern as
  // /ops/leads-review — gated by the lead backend's own login, never indexed.
  opsSessions: { path: '/ops/sessions', indexable: false },
  opsSessionNew: { path: '/ops/sessions/new', indexable: false },
  opsSessionEdit: { path: '/ops/sessions/:id/edit', indexable: false },
  // Webinar workflow page (Task #449): audience, invitations, reminders,
  // recording — same ops login as the sessions list.
  opsSessionWebinar: { path: '/ops/sessions/:id/webinar', indexable: false },
  // Internal ops tool (leads review, Task #223): lives on the public router so
  // it avoids the Microsoft sign-in wall, but it is gated by its own backend
  // login and must never be indexed or listed in the sitemap.
  opsLeadsReview: { path: '/ops/leads-review', indexable: false },
  // Per-user / PHI-linked pages: never indexed, never in the sitemap.
  unsubscribe: { path: '/unsubscribe/:leadId', indexable: false },
  molecularAck: {
    path: '/molecular-testing/:patientId/acknowledgement/:labTestProvider?',
    indexable: false,
  },
} satisfies Record<string, PublicRoute>;

export type PublicRouteName = keyof typeof PUBLIC_ROUTES;

/** True when a path pattern contains a route parameter (`:id`) and therefore
 *  can't be a concrete, crawlable URL. */
export function isParameterised(path: string): boolean {
  return path.includes(':');
}

/** Resolve a path (or already-absolute URL) to an absolute URL on SITE_URL. */
export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }

  const suffix = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;

  return `${SITE_URL}${suffix}`;
}

/** The concrete, indexable routes that belong in the sitemap. */
export function sitemapRoutes(): PublicRoute[] {
  return Object.values(PUBLIC_ROUTES).filter(
    route => route.indexable && !isParameterised(route.path)
  );
}

/** Render the sitemap XML from the shared route map. Used by the Vite plugin
 *  (build + dev) so the sitemap can never drift from the real routes. */
export function buildSitemapXml(): string {
  const urls = sitemapRoutes()
    .map(route => {
      const parts = [`    <loc>${absoluteUrl(route.path)}</loc>`];

      parts.push(`    <lastmod>${route.lastmod ?? SITE_LASTMOD}</lastmod>`);

      if (route.changefreq) {
        parts.push(`    <changefreq>${route.changefreq}</changefreq>`);
      }

      if (typeof route.priority === 'number') {
        parts.push(`    <priority>${route.priority.toFixed(1)}</priority>`);
      }

      return `  <url>\n${parts.join('\n')}\n  </url>`;
    })
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${urls}\n</urlset>\n`;
}
